/*
  Plays the vinyl animation from snippets/atc-vinyl-animation.liquid on add to cart and pre-order
  buttons.

  One click listener on the document, in the capture phase, so it sees every button without
  binding to each one: theme buttons, the sticky bar, product cards rendered later, and Fast Simon
  grid buttons that only appear once Fast Simon has drawn the collection. It never calls
  preventDefault or stopPropagation, so the add to cart runs exactly as before, at the same moment.

  Timeline (ms):
       0  label fades out (400 ms)
     120  record and sleeve fade in (200 ms); sleeve turns from edge-on to front (520 ms)
     320  record fully shown, standing still for a beat (as in the prototype)
     420  record starts spinning (780 ms) and rolls left to right into the sleeve (700 ms),
          "Added" revealed behind it
    1120  sleeve knock (320 ms)
    1440  UpCart's cart drawer is let out and slides in (it opened at the click, out of sight)
    1590  record, sleeve and "Added" fade out (250 ms) while the label fades back in (400 ms);
          on Fast Simon pills this waits until Fast Simon has put its own label back (~1.8 s)
    1990  layer removed (later on Fast Simon pills, by the same wait)

  With Reduce Motion on, there is no record or sleeve (the snippet CSS hides them): the label fades
  out while the check mark and "Added" fade in (0 to 400 ms), "Added" holds, the cart drawer is let
  out at 900 ms and the label fades back in from 1200 ms.
*/
(function () {
  if (window.atcVinylAnimation) return;

  const TEMPLATE_ID = 'AtcVinylAnimationTemplate';
  const HOST_CLASS = 'atc-vinyl-host';
  const LEAVING_CLASS = 'atc-vinyl-leaving';
  const LAYER_SELECTOR = '.atc-vinyl';
  // Theme buttons: product page, top buy button, sticky bar, product cards. Fast Simon draws its
  // grid button as a div; the container is the visible pill. Fast Simon's recommendation widgets
  // on the product page ("You may also like", "Recently viewed") use a plain <button> with no name
  // and no form, so they need their own selector.
  const BUTTON_SELECTOR = [
    'button[name="add"]',
    'form[action*="/cart/add"] [type="submit"]',
    '.sabhq-right-buy-button',
    '[data-sticky-add]',
    '.fs-add-to-cart-button-container',
    '.fs-recommendation-widget-products-container .add-to-cart-button',
    '.fs-recommendation-widget-products-container .add-to-cart-var-button',
  ].join(', ');
  // "Choose options" opens the quick-add modal; it does not add to cart.
  // Fast Simon keeps the same pill for sold-out and gift card products, styled through the parent.
  const EXCLUDE_SELECTOR = [
    'modal-opener [name="add"]',
    '[aria-disabled="true"]',
    '[disabled]',
    '.soldout-class .fs-add-to-cart-button-container',
    '.giftcard-class .fs-add-to-cart-button-container',
  ].join(', ');
  const LABEL_PATTERN = /add to cart|pre-?order|back ?order|buy now/i;
  const FADE_OUT_AT = 1590;
  // Long enough to read "Added" before the drawer covers the button (all of it on phones).
  const REDUCED_FADE_OUT_AT = 1200;
  const LAYER_FADE_OUT = 250;
  // Matches the label transition in .atc-vinyl-leaving.
  const LABEL_FADE_IN = 400;
  // UpCart opens its drawer the moment the add starts, which covers the button (all of it on
  // phones). The add itself is untouched; only the drawer and its backdrop are kept out of sight
  // until the record has landed and the sleeve has knocked, then slide in with UpCart's own
  // transition. HOLD_CART_CLASS goes on <html>; the CSS is in the snippet.
  const HOLD_CART_CLASS = 'atc-vinyl-hold-cart';
  const RELEASE_CART_CLASS = 'atc-vinyl-release-cart';
  const RELEASE_CART_AT = 1440;
  const REDUCED_RELEASE_CART_AT = 900;
  // UpCart's drawer slides in over 0.249 s; the release class only lives that long.
  const RELEASE_CART_DURATION = 300;
  // Fast Simon swaps its own label to "Adding..." then "Added!" and only restores it about 1.8 s
  // after the click. The ending waits for the button's own label to be back, up to this long, so
  // that text never fades in after the animation.
  const APP_LABEL_PATTERN = /adding|added/i;
  const APP_LABEL_MAX_WAIT = 1500;
  // The record fades in, rests for a beat once fully shown, then spins and rolls off together, as
  // in the prototype (the client asked for this pause). It must not stop again once it moves.
  // Spin and roll are separate animations on the rotate and translate properties, so each has
  // its own timing. The sleeve knock in the snippet CSS starts when the roll ends (DISC_START +
  // 700 ms); keep the two in step.
  const DISC_START = 420;
  const SPIN = { duration: 780, delay: DISC_START, fill: 'both', easing: 'cubic-bezier(.3, .4, .4, 1)' };
  const ROLL = { duration: 700, delay: DISC_START, fill: 'both', easing: 'cubic-bezier(.25, .1, .2, 1)' };

  // Fast Simon's recommendation widgets render inside an open shadow root. The click still reaches
  // the document listener (composedPath includes the shadow nodes), but the snippet's page styles
  // do not reach the layer, so a copy of them goes into the shadow root on its first click.
  const STYLES_ID = 'AtcVinylAnimationStyles';

  const template = document.getElementById(TEMPLATE_ID);
  if (!template) return;

  function ensureStyles(button) {
    const root = button.getRootNode();
    if (!(root instanceof ShadowRoot) || root.getElementById(STYLES_ID)) return;
    const styles = document.getElementById(STYLES_ID);
    if (styles) root.appendChild(styles.cloneNode(true));
  }

  const running = new WeakSet();
  let cartHolds = 0;

  // Several buttons can animate at once; the drawer is let out when the last one is done.
  function holdCart() {
    cartHolds += 1;
    document.documentElement.classList.remove(RELEASE_CART_CLASS);
    document.documentElement.classList.add(HOLD_CART_CLASS);
  }

  function releaseCart() {
    cartHolds = Math.max(0, cartHolds - 1);
    if (cartHolds) return;
    const root = document.documentElement;
    root.classList.add(RELEASE_CART_CLASS);
    root.classList.remove(HOLD_CART_CLASS);
    setTimeout(() => {
      if (!cartHolds) root.classList.remove(RELEASE_CART_CLASS);
    }, RELEASE_CART_DURATION);
  }

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function findButton(event) {
    const path = event.composedPath ? event.composedPath() : [event.target];
    for (const node of path) {
      if (!(node instanceof Element)) continue;
      if (!node.matches(BUTTON_SELECTOR)) continue;
      if (node.matches(EXCLUDE_SELECTOR)) return null;
      // Fast Simon labels pre-orders with a ::after, so its pill is matched by class alone.
      if (!node.classList.contains('fs-add-to-cart-button-container') && !LABEL_PATTERN.test(node.textContent)) return null;
      return node;
    }
    return null;
  }

  // The style of the label text itself: Fast Simon colours an inner element, not the pill.
  function labelStyle(button) {
    const walker = document.createTreeWalker(button, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => (node.textContent.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP),
    });
    const text = walker.nextNode();
    return getComputedStyle(text ? text.parentElement : button);
  }

  function play(button) {
    if (running.has(button)) return;
    const rect = button.getBoundingClientRect();
    if (rect.width < 60 || rect.height < 20) return;
    running.add(button);
    ensureStyles(button);
    holdCart();
    setTimeout(releaseCart, reducedMotion() ? REDUCED_RELEASE_CART_AT : RELEASE_CART_AT);

    const width = button.clientWidth;
    const height = button.clientHeight;
    const size = Math.round(Math.min(31, Math.max(16, height * 0.66)));
    const inset = Math.max(4, Math.round((height - size) / 2) + 2);
    const disc = size * 0.87;

    const label = labelStyle(button);
    const layer = template.content.firstElementChild.cloneNode(true);
    layer.style.setProperty('--atc-vinyl-size', size + 'px');
    layer.style.setProperty('--atc-vinyl-inset', inset + 'px');
    // The button text goes transparent while it animates; "Added" keeps the colour it had.
    layer.style.setProperty('--atc-vinyl-color', label.color);
    layer.style.fontSize = label.fontSize;
    button.appendChild(layer);
    button.classList.add(HOST_CLASS);

    // The fade-in runs here rather than as a CSS transition: the layer is created on the click, so
    // it has no earlier style to transition from and would appear at full opacity at once, then
    // sit there still until the spin starts.
    const animations = [layer.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, delay: 120, fill: 'both' })];
    if (!reducedMotion()) {
      const discStart = inset + 2;
      const discEnd = width - inset - size + (size - disc) / 2;
      const travel = discEnd - discStart;
      const discElement = layer.querySelector('.atc-vinyl__disc');
      animations.push(discElement.animate([{ rotate: '0deg' }, { rotate: '680deg' }], SPIN));
      animations.push(discElement.animate([{ translate: '0 0' }, { translate: `${travel}px 0` }], ROLL));
      // "Added" is clipped at the centre line of the disc, so it appears right behind it.
      const centreStart = discStart + disc / 2;
      const centreEnd = centreStart + travel;
      animations.push(
        layer.querySelector('.atc-vinyl__added').animate(
          [
            { clipPath: `inset(0 ${width - centreStart}px 0 0)` },
            { clipPath: `inset(0 ${width - centreEnd}px 0 0)` },
          ],
          ROLL
        )
      );
    }

    // The ending fades rather than cuts: the layer fades out on top while the label fades back in
    // underneath (the leaving class keeps the button clipped and carries the label transitions).
    // Only once both are done is the layer removed and the button left exactly as it was.
    const fadeOut = () => {
      button.classList.add(LEAVING_CLASS);
      button.classList.remove(HOST_CLASS);
      layer.animate([{ opacity: 1 }, { opacity: 0 }], { duration: LAYER_FADE_OUT, easing: 'ease-out', fill: 'forwards' });
      setTimeout(() => {
        animations.forEach((animation) => animation.cancel());
        layer.remove();
        button.classList.remove(LEAVING_CLASS);
        running.delete(button);
      }, LABEL_FADE_IN);
    };
    setTimeout(() => whenOwnLabelIsBack(button, layer, fadeOut), reducedMotion() ? REDUCED_FADE_OUT_AT : FADE_OUT_AT);
  }

  // Calls done once the button's own text (everything but the animation layer) no longer shows an
  // app's "Adding..." / "Added!" state, or after APP_LABEL_MAX_WAIT.
  function whenOwnLabelIsBack(button, layer, done) {
    const ownLabel = () =>
      [...button.childNodes]
        .filter((node) => node !== layer)
        .map((node) => node.textContent)
        .join('');
    if (!APP_LABEL_PATTERN.test(ownLabel())) {
      done();
      return;
    }
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      observer.disconnect();
      clearTimeout(timer);
      done();
    };
    const observer = new MutationObserver(() => {
      if (!APP_LABEL_PATTERN.test(ownLabel())) finish();
    });
    observer.observe(button, { childList: true, characterData: true, subtree: true });
    const timer = setTimeout(finish, APP_LABEL_MAX_WAIT);
  }

  document.addEventListener(
    'click',
    (event) => {
      const button = findButton(event);
      if (button) play(button);
    },
    true
  );

  window.atcVinylAnimation = { play };
})();
